// Item categories: types, a `useItemCategories()` hook and a picker every module can drop into a
// form. The category of a product used to be typed, and typed text drifts — "Furniture" and
// "furniture" split every report that groups by it. The list is right there, so offer it.
import { useApi } from "@keel/core";
import { Select, type SelectChange } from "../Select";
import { Field } from "../ui";

export interface ItemCategory {
  id: string;
  /** Upper-case, unique per tenant, fixed once created: products carry it. */
  code: string;
  name: string;
  parentId: string | null;
  parentCode: string | null;
  /** Set on this category itself; null means "whatever the parent says". */
  defaultPostingGroup: string | null;
  defaultUnitCode: string | null;
  /** What a product in this category actually gets: its own value, or the nearest ancestor's. */
  effectivePostingGroup: string | null;
  effectiveUnitCode: string | null;
  sortOrder: number;
  isActive: boolean;
  /** 0 for a top-level category, 1 for its children, and so on. */
  depth: number;
  /** The names from the root down: "Goods / Raw materials". */
  path: string;
  /** Active categories directly under this one — a parent with any cannot be retired. */
  activeChildren: number;
}

/** The tree in display order (parents first, children indented by `depth`). Retired ones on request. */
export function useItemCategories(includeInactive = false) {
  return useApi<ItemCategory[]>(`/setup/item-categories${includeInactive ? "?includeInactive=true" : ""}`);
}

export interface ItemCategorySelectProps {
  /** The chosen category's code. */
  value: string | null | undefined;
  onChange: (code: string) => void;
  /** Allow no category at all, with this label. */
  emptyLabel?: string;
  /** Codes to leave out — a category picking its own parent must not see itself or its subtree. */
  exclude?: string[];
  disabled?: boolean;
  placeholder?: string;
}

// Figure spaces (U+2007): the option label is rendered as ordinary text, and a browser collapses
// a run of plain spaces to nothing, which would leave the children flush with their parent.
const INDENT = "\u2007\u2007\u2007";

/**
 * The bare control. Children sit indented under their parent, so "Tables" reads as part of
 * "Furniture" rather than as a stray word in an alphabet. A retired category that is still the
 * current value stays in the list, greyed out, so an old product does not show an empty field.
 */
export function ItemCategorySelect({
  value,
  onChange,
  emptyLabel,
  exclude = [],
  disabled,
  placeholder = "Choose a category…",
}: ItemCategorySelectProps) {
  const all = useItemCategories(true).data ?? [];
  const shown = all.filter((c) => !exclude.includes(c.code) && (c.isActive || c.code === value));
  return (
    <Select
      value={value ?? ""}
      onChange={(e: SelectChange) => onChange(e.target.value)}
      disabled={disabled}
      placeholder={placeholder}
    >
      {emptyLabel && <option value="">{emptyLabel}</option>}
      {shown.map((c) => (
        <option key={c.code} value={c.code} disabled={!c.isActive}>
          {INDENT.repeat(c.depth)}
          {c.name}
          {c.isActive ? "" : " (retired)"}
        </option>
      ))}
    </Select>
  );
}

/** The same control with its label, and the full path of the chosen category as the hint. */
export function ItemCategoryField({
  label,
  hint,
  ...rest
}: ItemCategorySelectProps & { label: string; hint?: string }) {
  const chosen = (useItemCategories(true).data ?? []).find((c) => c.code === rest.value);
  return (
    <Field label={label} hint={chosen && chosen.depth > 0 ? chosen.path : hint}>
      <ItemCategorySelect {...rest} />
    </Field>
  );
}
